import { AppDispatch } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "../services/axiosWrapper";
import { orgTypeServiceUrl } from "../services/dataManager";
import { setOrgStructureData, type OrgStructureState } from "../reducers/orgStructure";
import type {
  OrgUnitsControllerResponse,
  OrgUnitsListRequest,
  SaveOrgUnitPayload,
  GetOrgTypesResponse,
  ReorderOrgStructureRequest,
  ReorderOrgStructureResponse,
  GetAllPositionUsersDataRequest,
  GetAllPositionUsersDataResponse,
} from "@ServerTypes/orgUnits/orgUnits.schema";
import type { Exact } from "@Helpers/types/exact";

export const getOrgstructureData =
  <T extends Exact<OrgUnitsListRequest, T>>(data: T) =>
  async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<OrgUnitsControllerResponse>(
      `${getBackendUrl()}/orgStructure/orgStructureController`,
      data,
    );
    if (response.data.status) {
      const updatedDataForOrgStructure: OrgStructureState = {
        completeData: response.data.result as OrgStructureState["completeData"],
      };

      (response.data.result as Array<Record<string, any>>).map((data: Record<string, any>) => {
        if (!Object.hasOwn(updatedDataForOrgStructure, data.typeId)) {
          updatedDataForOrgStructure[data.typeId] = data.type;
        }
        if (updatedDataForOrgStructure[data.type]) {
          updatedDataForOrgStructure[data.type] = [
            ...(updatedDataForOrgStructure[data.type] as any[]),
            {
              id: data.id,
              name: data.name,
            },
          ];
        } else {
          updatedDataForOrgStructure[data.type] = [
            {
              id: data.id,
              name: data.name,
            },
          ];
        }
      });

      dispatch(setOrgStructureData(updatedDataForOrgStructure));
    }
  };

// Dispatches an org-structure insert/update/remove to its dedicated REST endpoint (the former
// /orgStructureController action-dispatcher has been split). `action` selects the endpoint and is
// stripped from the request body; each endpoint validates its own dedicated schema.
const ORG_UNIT_ACTION_PATHS: Record<string, string> = {
  insert: "/orgStructure/insertOrgUnit",
  update: "/orgStructure/updateOrgUnit",
  remove: "/orgStructure/removeOrgUnit",
};
export const getOrgStructure = async (data: SaveOrgUnitPayload) => {
  const { action, ...body } = data;
  const path = ORG_UNIT_ACTION_PATHS[action] || "/orgStructure/removeOrgUnit";
  return await axiosWrapper.post<OrgUnitsControllerResponse>(`${getBackendUrl()}${path}`, body);
};

export const getOrgTypes = async () => {
  const response = await axiosWrapper.post<GetOrgTypesResponse>(orgTypeServiceUrl);
  return response;
};

export const reorderOrgStructure = async <T extends Exact<ReorderOrgStructureRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<ReorderOrgStructureResponse>(
    `${getBackendUrl()}/orgStructure/reorderOrgStructure`,
    data,
  );
  return response;
};

export const getAllPositionUsersData = async <T extends Exact<GetAllPositionUsersDataRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<GetAllPositionUsersDataResponse>(
    `${getBackendUrl()}/orgStructure/getAllPositionUsersData`,
    data,
  );
  return response;
};
